import { useState } from "react";
import { useJson, nf, plural, fmtDate, go, card, control, countryPath, entityPath, ipPath, Chip, KevBadge, Box, About, OnThisPage, CATEGORY_BY_SLUG } from "./shared";
import { BarList, ChoroplethMap, ColumnChart } from "./Charts";
import { ReportCard } from "./Reports";
import { CvssPill } from "./Vulns";

// Internet Watch: the questions GreyNoise and Shodan answer, from free sources (internet.js).
//   #/kb/ip-lookup and #/kb/ip/<ip>   one address: what's open on it, is it scanning, who runs it
//   #/kb/noise                        the day's busiest ports and sources; ?port= picks a port
//   #/kb/exposed                      what's on the feeds' IPs; ?port= ?product= ?tag= ?cve= ?family=

const VERDICTS = [
  { key: "reported", color: "#dc2626", label: "Reported as malicious", what: "In our indicator feeds, or listed in a published report." },
  { key: "noisy", color: "#ea580c", label: "Scanning the internet", what: "One of the 10,000 busiest sources SANS ISC sees, or reported to it at least five times in a day, or on three or more blocklists. This is what most firewall alerts are: it isn't after you in particular, and it isn't proof of harm." },
  { key: "listed", color: "#ca8a04", label: "On a blocklist", what: "One or two public blocklists name it. Weak on its own: blocklists have false positives." },
  { key: "scanner", color: "#2563eb", label: "Known research scanner", what: "Run by a project that maps the internet, such as Shodan, Censys or Shadowserver. Blocklists often name these, so it's usually safe to ignore." },
  { key: "quiet", color: "#71717a", label: "Nothing reported", what: "None of our sources report it scanning or attacking. That isn't the same as safe: a targeted attacker won't scan the whole internet." },
];
const VERDICT = Object.fromEntries(VERDICTS.map(v => [v.key, v]));
const TAG_TITLE = {
  honeypot: "Shodan thinks this is a honeypot: a trap that pretends to be vulnerable, so its open ports and CVEs mean little",
  "eol-os": "Running an operating system that no longer gets updates", "eol-product": "Running software that no longer gets updates",
  "self-signed": "Uses a self-signed certificate", cloud: "Hosted with a cloud provider", vpn: "A VPN endpoint", tor: "A Tor node", proxy: "A proxy", c2: "Shodan identified command-and-control software here",
  compromised: "Shodan found signs that this host has been compromised", pqc: "Offers post-quantum cryptography",
};

const note = (ui, text) => <span style={{ textTransform: "none", letterSpacing: 0, fontWeight: 500 }}>{text}</span>;
const dim = (ui, text) => <div style={{ fontSize: ui.fs(12), color: ui.B.textDim, lineHeight: 1.5 }}>{text}</div>;
const grid = (min) => ({ display: "grid", gridTemplateColumns: `repeat(auto-fit, minmax(min(100%, ${min}px), 1fr))`, gap: 14, alignItems: "start" });
const big = (ui, n, label) => <div><div style={{ fontSize: ui.fs(26), fontWeight: 900, color: ui.B.text, lineHeight: 1.1 }}>{nf(n)}</div><div style={{ fontSize: ui.fs(12), color: ui.B.textDim, marginTop: 2 }}>{label}</div></div>;
const PORTS_SHOWN = 40;
const PRODUCTS_SHOWN = 24;
const SOURCES_SHOWN = 25;
const DECOY_NOTE = "Probably a decoy: it answers on far too many ports, or Shodan tagged it a honeypot. Its ports and CVEs may mean little.";
const portLabel = (p) => (p.name ? `${p.port} ${p.name}` : String(p.port));
const mono = { fontFamily: "monospace" };
const shortDay = (k, short) => (short ? k.slice(5) : k);

function Heading({ slug, ui, about, children }) {
  const { B, fs } = ui;
  return (
    <div>
      <h2 style={{ fontSize: fs(22), fontWeight: 900, margin: "4px 0 6px", color: B.text }}>{CATEGORY_BY_SLUG[slug].label}</h2>
      <div style={{ color: B.textMuted, fontSize: fs(13), margin: "0 0 4px", maxWidth: "110ch", lineHeight: 1.6 }}>
        {children}
        {about && <About ui={ui} inline>{about}</About>}
      </div>
    </div>
  );
}

function Credit({ credit, ui, shodan, ipsum }) {
  const { B, fs } = ui;
  if (!credit) return null;
  const link = (c) => <a href={c.url} target="_blank" rel="noreferrer noopener" style={{ color: B.accent }}>{c.source}</a>;
  return (
    <div style={{ fontSize: fs(11), color: B.textDim, lineHeight: 1.6 }}>
      Data: {link(credit.isc)} (<a href={credit.isc.licenseUrl} target="_blank" rel="noreferrer noopener" style={{ color: B.accent }}>{credit.isc.license}</a>){shodan && <>, {link(credit.shodan)}</>}{ipsum && <>, {link(credit.ipsum)} ({credit.ipsum.license})</>}, for non-commercial use.
      {" "}ISC's figures are unfiltered reports from volunteers' firewalls and sensors: it asks that they aren't used as a blocklist, and neither should these pages be.
    </div>
  );
}

function IpSearch({ ui, value = "" }) {
  const { B, fs, HP } = ui;
  const [q, setQ] = useState(value);
  const submit = (e) => {
    e.preventDefault();
    const ip = q.trim().replace(/\[\.\]/g, ".");
    if (ip) go(ipPath(ip));
  };
  return (
    <form onSubmit={submit} style={{ display: "flex", gap: 8, flexWrap: "wrap", maxWidth: 520 }}>
      <input value={q} onChange={e => setQ(e.target.value)} placeholder="An IPv4 address, like 203.0.113.7" aria-label="IP address" style={{ ...control(ui), flex: "1 1 220px", minWidth: 0, fontSize: fs(14), color: B.text, ...mono }} />
      <button type="submit" style={{ ...HP.primaryBtn, padding: "8px 18px" }}>Look up</button>
    </form>
  );
}

// ─── #/kb/ip-lookup ───

export function KbIpLookup({ ui }) {
  const { B, fs } = ui;
  const { data } = useJson("/api/kb/internet/noise");
  const noisy = data?.sources?.find(s => !s.scanner);
  const scanner = data?.sources?.find(s => s.scanner);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <Heading slug="ip-lookup" ui={ui} about="Open ports, software and CVEs come from Shodan InternetDB (updated weekly). Activity comes from SANS ISC's sensors, and blocklist counts from IPsum (both daily). Idle Hour doesn't scan anything itself. IPv4 only for now.">
        Paste an address from a firewall alert or a report. Find out whether it's scanning everyone or just you, what's open on it, who runs it, and what we already know about it.
      </Heading>
      <IpSearch ui={ui} />
      {(noisy || scanner) && (
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", fontSize: fs(12), color: B.textDim }}>
          Try:
          {noisy && <a href={ipPath(noisy.ip)} style={{ color: B.accent, ...mono }}>{noisy.ip}</a>}{noisy && <span>a busy source yesterday</span>}
          {scanner && <a href={ipPath(scanner.ip)} style={{ color: B.accent, ...mono }}>{scanner.ip}</a>}{scanner && <span>a research scanner</span>}
        </div>
      )}
      <Box title="What the labels mean" ui={ui}>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {VERDICTS.map(v => (
            <div key={v.key} style={{ display: "flex", gap: 10, alignItems: "baseline", flexWrap: "wrap", fontSize: fs(12), lineHeight: 1.5 }}>
              <span style={{ flex: "0 0 180px" }}><Chip ui={ui} color={v.color}>{v.label}</Chip></span>
              <span style={{ flex: "1 1 280px", color: B.textMuted }}>{v.what}</span>
            </div>
          ))}
        </div>
      </Box>
    </div>
  );
}

// ─── #/kb/ip/<ip> ───

function VerdictCard({ d, ui }) {
  const { B, fs } = ui;
  const v = VERDICT[d.verdict.key];
  const a = d.activity;
  return (
    <div style={{ ...card(ui), borderLeft: `4px solid ${v.color}`, padding: "14px 18px" }}>
      <div style={{ fontSize: fs(18), fontWeight: 800, color: v.color, marginBottom: 6 }}>{d.verdict.label}</div>
      {d.verdict.why.length > 0
        ? <ul style={{ margin: 0, paddingLeft: 18, fontSize: fs(13), color: B.textMuted, lineHeight: 1.6 }}>{d.verdict.why.map(w => <li key={w}>{w}</li>)}</ul>
        : <div style={{ fontSize: fs(13), color: B.textMuted, lineHeight: 1.6 }}>{v.what}</div>}
      {a?.asName && (
        <div style={{ fontSize: fs(12), color: B.textDim, marginTop: 10, lineHeight: 1.6 }}>
          {a.asName}{a.asn ? ` (AS${a.asn})` : ""}{a.network ? ` · ${a.network}` : ""}
          {a.country && <> · <a href={countryPath(a.country)} style={{ color: B.accent }}>{a.countryName || a.country}</a></>}
          {a.abuse ? ` · abuse contact ${a.abuse}` : ""}
        </div>
      )}
    </div>
  );
}

function HostBox({ d, ui }) {
  const { B, fs } = ui;
  const h = d.host;
  const label = (text) => <div style={{ fontSize: fs(11), color: B.textDim, margin: "10px 0 5px" }}>{text}</div>;
  return (
    <Box title="What's open on it" ui={ui} right={note(ui, "Shodan InternetDB, updated weekly")}>
      {!h && dim(ui, d.failed.includes("Shodan InternetDB") ? "Shodan InternetDB didn't answer. Try again in a few minutes." : "Shodan has no record of this address: nothing was open when it last looked, or it hasn't looked yet.")}
      {h && (
        <>
          {h.decoy && <div style={{ fontSize: fs(12), color: VERDICT.listed.color, lineHeight: 1.5, marginBottom: 8 }}>{DECOY_NOTE}</div>}
          <div style={{ display: "flex", gap: 5, flexWrap: "wrap", alignItems: "center" }}>
            {h.ports.slice(0, PORTS_SHOWN).map(p => <a key={p.port} href={`#/kb/noise?port=${p.port}`} style={{ textDecoration: "none" }}><Chip ui={ui}>{portLabel(p)}</Chip></a>)}
            {h.ports.length > PORTS_SHOWN && <span style={{ fontSize: fs(12), color: B.textDim }}>and {nf(h.ports.length - PORTS_SHOWN)} more</span>}
            {!h.ports.length && dim(ui, "No open ports listed.")}
          </div>
          {h.products.length > 0 && <>{label("Software")}<div style={{ display: "flex", gap: 5, flexWrap: "wrap", alignItems: "center" }}>{h.products.slice(0, PRODUCTS_SHOWN).map(p => <Chip key={p} ui={ui}>{p}</Chip>)}{h.products.length > PRODUCTS_SHOWN && <span style={{ fontSize: fs(12), color: B.textDim }}>and {nf(h.products.length - PRODUCTS_SHOWN)} more</span>}</div></>}
          {h.tags.length > 0 && <>{label("Shodan's tags")}<div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>{h.tags.map(t => <Chip key={t} ui={ui} title={TAG_TITLE[t]}>{t}</Chip>)}</div></>}
          {h.hostnames.length > 0 && <>{label("Hostnames")}<div style={{ fontSize: fs(12), color: B.textMuted, wordBreak: "break-all", lineHeight: 1.6, ...mono }}>{h.hostnames.slice(0, 12).join(", ")}{h.hostnames.length > 12 ? ` and ${h.hostnames.length - 12} more` : ""}</div></>}
        </>
      )}
    </Box>
  );
}

function CveBox({ host, ui }) {
  const { B, fs, getConf } = ui;
  const exploited = host.cves.filter(c => c.kev).length;
  return (
    <Box title={`${plural(host.cveTotal, "CVE")} matched`} ui={ui} right={note(ui, exploited ? `${exploited} exploited in the wild (CISA KEV)` : "")}
      about="Shodan matches CVEs from the version numbers a service gives out; nobody tested this host, and vendors often fix a bug without changing the version. Worst first: used in ransomware, exploited in the wild, then by CVSS score.">
      <div style={{ display: "flex", flexDirection: "column" }}>
        {host.cves.map(c => (
          <div key={c.name} style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", padding: "7px 0", borderTop: `1px solid ${B.border}`, fontSize: fs(12) }}>
            <a href={entityPath(c)} style={{ color: B.text, fontWeight: 700, textDecoration: "none", ...mono }}>{c.name}</a>
            <KevBadge kev={c.kev} ui={ui} />
            {c.cvss && <CvssPill cvss={c.cvss} ui={ui} />}
            {c.product && <span style={{ color: B.textDim }}>{c.product}</span>}
            {c.groups?.map(g => <a key={g.id} href={entityPath(g)} style={{ textDecoration: "none" }}><Chip ui={ui} color={getConf(g.type).color}>{g.name}</Chip></a>)}
          </div>
        ))}
      </div>
      {host.cveTotal > host.cves.length && dim(ui, `The worst ${host.cves.length} of ${nf(host.cveTotal)}.`)}
    </Box>
  );
}

function ActivityBox({ d, ui }) {
  const { B, fs } = ui;
  const a = d.activity;
  const { busy, recent } = d;
  const label = (text) => <div style={{ fontSize: fs(11), color: B.textDim, margin: "12px 0 5px" }}>{text}</div>;
  return (
    <Box title="What it's been doing" ui={ui} right={note(ui, "SANS ISC and IPsum, daily")}>
      {d.failed.includes("SANS ISC") && dim(ui, "SANS ISC didn't answer. Try again in a few minutes.")}
      <div style={grid(130)}>
        {big(ui, recent?.reports || 0, `reports${recent?.date ? ` on ${fmtDate(recent.date)}` : " yesterday"}`)}
        {busy && big(ui, busy.targets, "addresses it's hit")}
        {big(ui, d.lists, d.lists === 1 ? "public blocklist names it" : "public blocklists name it")}
      </div>
      {busy && <div style={{ fontSize: fs(12), color: B.textDim, marginTop: 10 }}>Seen by ISC from {fmtDate(busy.firstSeen)} to {fmtDate(busy.lastSeen)}.</div>}
      {recent?.replies > 0 && <div style={{ fontSize: fs(12), color: B.textDim, marginTop: 10 }}>{plural(recent.replies, "more report")} look like a server's replies or pings, not scans, so they aren't counted.</div>}
      {recent?.ports.length > 0 && (
        <>
          {label(recent.portCount > recent.ports.length ? `Ports it went for (the top ${recent.ports.length} of ${nf(recent.portCount)})` : "Ports it went for")}
          <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
            {recent.ports.map(p => <a key={p.port} href={`#/kb/noise?port=${p.port}`} style={{ textDecoration: "none" }}><Chip ui={ui} title={plural(p.count, "report")}>{portLabel(p)}</Chip></a>)}
          </div>
        </>
      )}
      {(a?.ssh?.attempts > 0 || a?.web?.count > 0) && (
        <div style={{ fontSize: fs(12), color: B.textDim, marginTop: 10, lineHeight: 1.6 }}>
          {a.ssh?.attempts > 0 && <div>{plural(a.ssh.attempts, "SSH login attempt")} on ISC's honeypots, {fmtDate(a.ssh.start)} to {fmtDate(a.ssh.end)}.</div>}
          {a.web?.count > 0 && <div>{plural(a.web.count, "request")} to ISC's web honeypots, last on {fmtDate(a.web.lastSeen)}.</div>}
        </div>
      )}
      {a?.feeds.length > 0 && (
        <>
          {label("ISC's lists it's on")}
          <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
            {a.feeds.map(f => <Chip key={f.type} ui={ui} title={f.lastSeen ? `Last listed ${f.lastSeen}` : undefined}>{f.name}</Chip>)}
          </div>
        </>
      )}
    </Box>
  );
}

function KnownBox({ known, ui }) {
  const { B, fs, getConf } = ui;
  const o = known.indicator;
  return (
    <Box title="In Idle Hour" ui={ui}>
      {o && (
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", fontSize: fs(12), marginBottom: known.reports.length ? 12 : 0 }}>
          <a href={entityPath(o)} style={{ color: B.accent, fontWeight: 700 }}>Open the indicator →</a>
          {o.families.map(f => <a key={f.id} href={`#/kb/e/${encodeURIComponent(f.id)}`} style={{ textDecoration: "none" }}><Chip ui={ui} color={getConf("malware").color}>{f.name}</Chip></a>)}
          {o.lastSeen && <span style={{ color: B.textDim }}>last seen {fmtDate(o.lastSeen)}</span>}
        </div>
      )}
      {known.reports.length > 0 && <div style={grid(280)}>{known.reports.slice(0, 6).map(r => <ReportCard key={r.id} s={r} ui={ui} />)}</div>}
    </Box>
  );
}

export function KbIp({ ip, ui }) {
  const { B, fs } = ui;
  const { data: d, error, loading } = useJson(`/api/kb/ip/${encodeURIComponent(ip)}`);
  const known = d && (d.known.indicator || d.known.reports.length);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <IpSearch key={ip} ui={ui} value={ip} />
      <h2 style={{ fontSize: fs(24), fontWeight: 800, margin: 0, color: B.text, wordBreak: "break-all", ...mono }}>{ip}</h2>
      {loading && <div style={{ color: B.textDim, fontSize: fs(13) }}>Asking Shodan InternetDB and SANS ISC…</div>}
      {error && <div style={{ color: "#dc2626", fontSize: fs(12) }}>{error}</div>}
      {d && (
        <>
          <VerdictCard d={d} ui={ui} />
          {d.failed.length > 0 && dim(ui, `${d.failed.join(" and ")} didn't answer, so this may be missing something.`)}
          {known ? <KnownBox known={d.known} ui={ui} /> : null}
          <div style={grid(340)}>
            <HostBox d={d} ui={ui} />
            <ActivityBox d={d} ui={ui} />
          </div>
          {d.host?.cveTotal > 0 && <CveBox host={d.host} ui={ui} />}
          <Credit credit={d.credit} ui={ui} shodan ipsum />
        </>
      )}
    </div>
  );
}

// ─── #/kb/noise ───

function keepPort(port) {
  const q = new URLSearchParams(window.location.hash.split("?")[1] || "");
  q.set("port", port);
  window.history.replaceState(null, "", `#/kb/noise?${q}`);
}

function PortHistory({ port, ui, onPick }) {
  const { B, fs } = ui;
  const { data, error, loading } = useJson(port === null ? null : `/api/kb/internet/port/${port}`);
  const [other, setOther] = useState("");
  const submit = (e) => {
    e.preventDefault();
    const n = Number(other);
    if (other !== "" && Number.isInteger(n) && n >= 0 && n <= 65535) { onPick(n); setOther(""); }
  };
  const find = (
    <form onSubmit={submit} style={{ display: "inline-flex", gap: 6 }}>
      <input value={other} onChange={e => setOther(e.target.value.replace(/\D/g, ""))} placeholder="Any port" aria-label="Show a port" inputMode="numeric" style={{ ...control(ui), width: 90, padding: "4px 8px", textTransform: "none", letterSpacing: 0 }} />
    </form>
  );
  return (
    <Box title={`Port ${port ?? ""}${data?.name ? ` · ${data.name}` : ""} over 30 days`} jump="Port history" ui={ui} right={find}>
      {loading && <div style={{ color: B.textDim, fontSize: fs(13) }}>Loading…</div>}
      {error && <div style={{ color: "#dc2626", fontSize: fs(12) }}>{error}</div>}
      {data && (data.days.length
        ? <ColumnChart points={data.days.map(d => ({ key: d.date, value: d.sources }))} ui={ui} unit="source addresses" formatLabel={shortDay} />
        : dim(ui, "ISC has no reports for this port in the last 30 days."))}
    </Box>
  );
}

function SourcesTable({ sources, topCount, scannersInTop, ui }) {
  const { B, fs, HP } = ui;
  const [all, setAll] = useState(false);
  const [long, setLong] = useState(false);
  const rows = sources.filter(s => all || !s.scanner);
  const cell = { padding: "7px 10px", borderTop: `1px solid ${B.border}`, fontSize: fs(12), textAlign: "right", fontVariantNumeric: "tabular-nums", color: B.textMuted, verticalAlign: "top" };
  const left = { ...cell, textAlign: "left" };
  const th = (label, isLeft) => <th style={{ padding: "9px 10px", fontWeight: 700, textAlign: isLeft ? "left" : "right", whiteSpace: "nowrap" }}>{label}</th>;
  const toggle = <button onClick={() => setAll(a => !a)} aria-pressed={all} style={{ ...HP.navBtn, padding: "3px 10px", textTransform: "none", letterSpacing: 0 }}>{all ? "Hide research scanners" : "Show research scanners"}</button>;
  return (
    <Box title="The busiest sources" jump="Busiest sources" ui={ui} right={toggle}>
      {dim(ui, `${scannersInTop} of the busiest ${topCount} are research scanners, which is why a scan in your logs so often turns out to be harmless. They're hidden here until you ask for them.`)}
      <div style={{ overflowX: "auto", marginTop: 8 }}>
        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 760 }}>
          <thead><tr style={{ fontSize: fs(10), color: B.textDim, textTransform: "uppercase", letterSpacing: 1 }}>{th("Address", true)}{th("Network", true)}{th("Country", true)}{th("Addresses hit")}{th("Packets")}{th("What else", true)}</tr></thead>
          <tbody>
            {rows.slice(0, long ? rows.length : SOURCES_SHOWN).map(s => (
              <tr key={s.ip}>
                <td style={left}><a href={ipPath(s.ip)} style={{ color: B.text, fontWeight: 700, textDecoration: "none", ...mono }}>{s.ip}</a></td>
                <td style={{ ...left, maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={s.asName || undefined}>{s.asName || s.scanner || "–"}</td>
                <td style={left}>{s.country ? <a href={countryPath(s.country)} style={{ color: B.textMuted }}>{s.country}</a> : "–"}</td>
                <td style={cell}>{nf(s.targets)}</td>
                <td style={cell}>{nf(s.reports)}</td>
                <td style={left}>
                  <span style={{ display: "inline-flex", gap: 4, flexWrap: "wrap" }}>
                    {s.scanner && <Chip ui={ui} color={VERDICT.scanner.color}>{s.scanner}</Chip>}
                    {s.inFeeds && <Chip ui={ui} color={VERDICT.reported.color}>In our feeds</Chip>}
                    {s.lists > 0 && <Chip ui={ui} title="Public blocklists that name it (IPsum)">{plural(s.lists, "blocklist")}</Chip>}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {rows.length > SOURCES_SHOWN && <button onClick={() => setLong(l => !l)} aria-expanded={long} style={{ ...HP.navBtn, marginTop: 12 }}>{long ? "Show fewer" : `Show all ${rows.length}`}</button>}
    </Box>
  );
}

export function KbNoise({ ui, params = {} }) {
  const { B, fs } = ui;
  const { data, error, loading } = useJson("/api/kb/internet/noise");
  const asked = /^\d{1,5}$/.test(params.port || "") ? Number(params.port) : null;
  const [picked, setPicked] = useState(asked);
  const heading = (
    <Heading slug="noise" ui={ui} about="SANS ISC collects firewall logs and sensor data from volunteers all over the world. Everything on the internet gets scanned all day; this page shows that background noise, so a scan in your own logs can be judged against it. A source's network and country are those of the network it's on, as registered, not where the attacker sits.">
      Who's knocking on every door on the internet, and what they're trying.
    </Heading>
  );
  if (loading) return <>{heading}<div style={{ color: B.textDim, fontSize: fs(13) }}>Loading…</div></>;
  if (error) return <>{heading}<div style={{ color: "#dc2626", fontSize: fs(12) }}>{error}</div></>;
  if (!data.builtAt) return <>{heading}{dim(ui, data.error ? `SANS ISC couldn't be reached (${data.error}); trying again within the hour.` : "Loading… these figures arrive a few minutes after the server starts.")}</>;

  const port = picked ?? data.ports[0]?.port ?? null;
  const pick = (p) => { setPicked(p); keepPort(p); };
  const day = data.days.find(d => d.date === data.date);
  const lastDay = data.days[data.days.length - 1]?.date;
  const values = Object.fromEntries(data.countries.map(c => [c.iso2, c.value]));
  const numeric = Object.fromEntries(data.countries.filter(c => c.numeric).map(c => [c.iso2, c.numeric]));
  const names = Object.fromEntries(data.countries.map(c => [c.iso2, c.name]));
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div>{heading}<OnThisPage ui={ui} /></div>
      <Box title={`The internet on ${fmtDate(data.date)}`} jump="The day" ui={ui} right={note(ui, "SANS ISC's sensors")}>
        <div style={{ ...grid(150), marginBottom: 14 }}>
          {big(ui, day?.sources, "source addresses")}
          {big(ui, day?.records, "packets reported")}
          {big(ui, data.scannerCount, "known research scanners")}
          {big(ui, data.blocklists.threePlus, "addresses on 3+ blocklists")}
        </div>
        <ColumnChart points={data.days.map(d => ({ key: d.date, value: d.sources }))} ui={ui} unit="source addresses" formatLabel={shortDay} partialKey={lastDay > data.date ? lastDay : null} />
      </Box>
      <div style={grid(340)}>
        <Box title="Busiest ports" ui={ui} right={note(ui, "pick one to see its month")}>
          <BarList rows={data.ports.map(p => ({ label: portLabel(p), value: p.sources, port: p.port }))} ui={ui} unit="sources" maxRows={12} keepOrder onSelect={(r) => pick(r.port)} />
        </Box>
        <PortHistory port={port} ui={ui} onPick={pick} />
      </div>
      <SourcesTable sources={data.sources} topCount={data.topCount} scannersInTop={data.scannersInTop} ui={ui} />
      <div style={grid(340)}>
        <Box title="Where the rest are" jump="Countries" ui={ui} right={note(ui, `the ${data.unknownCount} busiest that aren't research scanners`)}>
          <ChoroplethMap mode="count" values={values} numeric={numeric} names={names} unit="addresses" tools={false} ui={ui} onSelect={(iso2) => go(countryPath(iso2))} />
        </Box>
        <Box title="Their networks" jump="Networks" ui={ui}>
          <BarList rows={data.networks} ui={ui} unit="addresses" maxRows={10} />
        </Box>
      </div>
      <div style={grid(340)}>
        {data.industrial.length > 0 && (
          <Box title="Industrial ports" ui={ui} right={note(ui, "sources a day, last 30 days")} about="Ports used only by industrial control systems. Scanning them doesn't mean an attack on a plant: research scanners sweep these too. See ICS / SCADA for real incidents.">
            <BarList rows={data.industrial.map(p => ({ label: portLabel(p), value: p.avgSources, port: p.port }))} ui={ui} unit="sources a day" maxRows={8} onSelect={(r) => pick(r.port)} />
          </Box>
        )}
        <Box title="Who the research scanners are" jump="Research scanners" ui={ui} right={note(ui, `${nf(data.scannerCount)} addresses`)} about="ISC's list of projects that scan the whole internet for research or to sell the results. If one of these addresses is in your logs, it's mapping everyone, not coming for you.">
          <BarList rows={data.orgs} ui={ui} unit="addresses" maxRows={12} />
        </Box>
      </div>
      <Credit credit={data.credit} ui={ui} ipsum />
    </div>
  );
}

// ─── #/kb/exposed ───

const FILTERS = [["port", "Port"], ["product", "Software"], ["tag", "Tag"], ["cve", "CVE"], ["family", "Malware"]];
const asQuery = (params) => new URLSearchParams(Object.entries(params).filter(([k, v]) => v && FILTERS.some(([f]) => f === k)));
function filterPath(params, key, value) {
  const q = asQuery(params);
  if (q.get(key) === String(value)) q.delete(key); else q.set(key, value);
  return `#/kb/exposed${q.toString() ? `?${q}` : ""}`;
}

function Facet({ title, items, ui, param, params, keyOf = (i) => i.label, labelOf = (i) => i.label, hot }) {
  const { B, fs } = ui;
  if (!items.length) return null;
  return (
    <Box title={title} ui={ui}>
      <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
        {items.map(i => (
          <a key={keyOf(i)} href={filterPath(params, param, keyOf(i))} style={{ textDecoration: "none" }}>
            <Chip ui={ui} color={hot?.(i) ? VERDICT.reported.color : undefined}>{labelOf(i)} <span style={{ color: B.textDim, fontSize: fs(10) }}>{nf(i.value)}</span></Chip>
          </a>
        ))}
      </div>
    </Box>
  );
}

export function KbExposed({ ui, params = {} }) {
  const { B, fs, HP, getConf } = ui;
  const [limit, setLimit] = useState(50);
  const q = asQuery(params);
  q.set("limit", limit);
  const { data, error, loading } = useJson(`/api/kb/internet/tracked?${q}`);
  const active = FILTERS.filter(([k]) => params[k]);
  const heading = (
    <Heading slug="exposed" ui={ui} about="Shodan's search across the internet is a paid product; this is a smaller version of it, over the addresses we already follow. Each IP in the indicator feeds is looked up in Shodan InternetDB, one every two seconds, and again after a week. CVEs come from version numbers, not from testing the host, and a server that's gone offline can keep its old record for a while.">
      What's running on the IP addresses in our indicator feeds: which C2 servers have Remote Desktop open, what software they run, and what's unpatched on them. Pick anything to filter by it.
    </Heading>
  );
  if (error) return <>{heading}<div style={{ color: "#dc2626", fontSize: fs(12) }}>{error}</div></>;
  if (!data) return <>{heading}<div style={{ color: B.textDim, fontSize: fs(13) }}>{loading ? "Loading…" : ""}</div></>;
  const cell = { padding: "7px 10px", borderTop: `1px solid ${B.border}`, fontSize: fs(12), color: B.textMuted, verticalAlign: "top", textAlign: "left" };
  const th = (label) => <th style={{ padding: "9px 10px", fontWeight: 700, textAlign: "left", whiteSpace: "nowrap" }}>{label}</th>;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {heading}
      {dim(ui, data.checked < data.total
        ? `Checked ${nf(data.checked)} of ${nf(data.total)} feed addresses so far${data.sweeping ? ", and still going" : ""}; ${nf(data.withData)} have a Shodan record.`
        : `All ${nf(data.total)} feed addresses checked; ${nf(data.withData)} have a Shodan record.`)}
      {active.length > 0 && (
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center", fontSize: fs(12), color: B.textDim }}>
          Showing:
          {active.map(([k, label]) => <a key={k} href={filterPath(params, k, params[k])} title="Remove this filter" style={{ textDecoration: "none" }}><Chip ui={ui} color={B.primary}>{label}: {params[k]} ×</Chip></a>)}
          <a href="#/kb/exposed" style={{ color: B.accent }}>Clear all</a>
        </div>
      )}
      <div style={grid(300)}>
        <Facet title="Open ports" items={data.facets.ports} ui={ui} param="port" params={params} keyOf={i => i.port} labelOf={portLabel} />
        <Facet title="Software" items={data.facets.products} ui={ui} param="product" params={params} />
        <Facet title="Malware" items={data.facets.families} ui={ui} param="family" params={params} />
        <Facet title="Shodan's tags" items={data.facets.tags} ui={ui} param="tag" params={params} />
        <Facet title="CVEs" items={data.facets.cves} ui={ui} param="cve" params={params} hot={i => i.kev} />
      </div>
      <Box title={plural(data.matched, "address", "addresses")} jump="Addresses" ui={ui} right={note(ui, "exploited CVEs first, likely decoys last")}>
        {data.rows.length === 0 && dim(ui, data.withData ? "Nothing matches all of those filters." : "The first check starts a few minutes after the server does.")}
        {data.rows.length > 0 && (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 820 }}>
              <thead><tr style={{ fontSize: fs(10), color: B.textDim, textTransform: "uppercase", letterSpacing: 1 }}>{th("Address")}{th("Malware")}{th("Open ports")}{th("Software")}{th("CVEs")}{th("Last seen")}</tr></thead>
              <tbody>
                {data.rows.map(r => (
                  <tr key={r.ip}>
                    <td style={cell}><a href={ipPath(r.ip)} style={{ color: B.text, fontWeight: 700, textDecoration: "none", ...mono }}>{r.ip}</a>{r.decoy && <div style={{ marginTop: 3 }}><Chip ui={ui} color={VERDICT.listed.color} title={DECOY_NOTE}>Likely decoy</Chip></div>}</td>
                    <td style={cell}><span style={{ display: "inline-flex", gap: 4, flexWrap: "wrap" }}>{r.families.slice(0, 3).map(f => <a key={f.id} href={`#/kb/e/${encodeURIComponent(f.id)}`} style={{ textDecoration: "none" }}><Chip ui={ui} color={getConf("malware").color}>{f.name}</Chip></a>)}</span></td>
                    <td style={cell}>{r.ports.slice(0, 8).map(p => p.port).join(", ")}{r.ports.length > 8 ? ` +${r.ports.length - 8}` : ""}</td>
                    <td style={cell}>{r.products.slice(0, 3).join(", ") || "–"}{r.products.length > 3 ? ` +${r.products.length - 3}` : ""}</td>
                    <td style={{ ...cell, whiteSpace: "nowrap" }}>{r.cves ? nf(r.cves) : "–"}{r.kev > 0 && <span style={{ color: VERDICT.reported.color, fontWeight: 700 }}> · {r.kev} exploited</span>}</td>
                    <td style={{ ...cell, whiteSpace: "nowrap" }}>{fmtDate(r.lastSeen) || "–"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {data.rows.length < data.matched && limit < 200 && <button onClick={() => setLimit(l => Math.min(200, l + 50))} style={{ ...HP.navBtn, marginTop: 12 }}>Show more</button>}
        {data.rows.length < data.matched && limit >= 200 && dim(ui, `The first ${data.rows.length}; filter to narrow it down.`)}
      </Box>
      <div style={{ fontSize: fs(11), color: B.textDim, lineHeight: 1.6 }}>
        Data: <a href={data.credit.shodan.url} target="_blank" rel="noreferrer noopener" style={{ color: B.accent }}>{data.credit.shodan.source}</a>, for non-commercial use. Addresses from the indicator feeds.
      </div>
    </div>
  );
}
